import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, where, orderBy, limit, getDocs } from './firestore-compat';
import { Search, Calendar, BarChart2, Upload, FileText, ChevronDown, ChevronUp, Users, AlertCircle, Clock, ArrowLeft } from 'lucide-react';
import VisitasEspecialidadeHub from './VisitasEspecialidadeHub';
import { isGrupo, getGrupoConfig } from './visitasGrupos';
import { ultimoConsolidado, linhasDaEspecialidade, dataDoUpload } from './visitasConsolidado';

const COLL = 'visitas_domiciliares';

// Duas convenções de cabeçalho convivem nos uploads: a "amigável" gerada pela
// sincronização automática do FTP, e a usada nos CSVs preparados manualmente
// pela equipe há meses. Cada campo tenta a primeira e cai pra segunda.
const COL_ALIASES = {
  pac:  ['Paciente'],
  op:   ['Operadora', 'Empresa'],
  st:   ['Status'],
  esp:  ['Cod. Especialidade', 'Especialidade'],
  fr:   ['Frequência', 'FREQUENCIA', 'Frequencia'],
  prof: ['Profiss. Agendado', 'Nome do Profissional'],
  de:   ['Agendar de:'],
  ate:  ['Agendar até:'],
  re:   ['Data Realiz.'],
  fil:  ['Filial'],
};

function pick(row, keys) {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
}

// specialty color palette (stable, muted-but-distinct)
const PALETTE = [
  '#0F766E', '#2563EB', '#9333EA', '#DB2777', '#D4351C', '#B5790B',
  '#15803D', '#0891B2', '#7C3AED', '#BE123C', '#4D7C0F', '#A16207',
  '#0E7490', '#9D174D', '#1D4ED8'
];
const specColorCache = {};
function specColor(name) {
  if (specColorCache[name] !== undefined) return specColorCache[name];
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  const c = PALETTE[h % PALETTE.length];
  specColorCache[name] = c;
  return c;
}

// ---- date helpers (dd/mm/yyyy [hh:mm]) ----
function parseDate(s) {
  if (!s) return null;
  const m = s.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!m) return null;
  return new Date(+m[3], +m[2] - 1, +m[1]);
}
function fmtShort(d) {
  return d ? String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0') : '—';
}
function fmtFull(d) {
  return d ? String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + d.getFullYear() : '';
}
function daysBetween(a, b) {
  const d1 = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const d2 = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round(Math.abs(d1 - d2) / 86400000);
}

const getLocalTodayISO = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStatusChipStyle = (status) => {
  const s = String(status || '').toUpperCase().trim();
  if (s === 'EM ATENDIMENTO' || s === 'ATENDIMENTO') {
    return { background: '#e6f4f2', color: '#0F766E', borderColor: '#bdf0e9' };
  }
  if (s === 'EM AVALIAÇÃO' || s === 'AVALIAÇÃO' || s === 'AVALIACAO') {
    return { background: '#fffbeb', color: '#B5790B', borderColor: '#fde68a' };
  }
  if (s === 'HOSPITALIZADO') {
    return { background: '#e0f2fe', color: '#0369a1', borderColor: '#bae6fd' };
  }
  if (s === 'OBITO' || s === 'ÓBITO' || s === 'CANCELADO') {
    return { background: '#fef2f2', color: '#D4351C', borderColor: '#fecaca' };
  }
  return { background: '#f8fafc', color: '#64748b', borderColor: '#e2e8f0' };
};

// Ponto de entrada da rota /dashboard-visitas: agora o upload vira uma planilha por
// especialidade, então antes de mostrar o painel a gente pede pra escolher qual.
export default function DashboardVisitas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const espParam = searchParams.get('esp') || '';

  if (!espParam) {
    return <VisitasEspecialidadeHub onSelect={esp => setSearchParams({ esp })} />;
  }
  return <DashboardVisitasDetalhe especialidade={espParam} onVoltar={() => setSearchParams({})} />;
}

function DashboardVisitasDetalhe({ especialidade, onVoltar }) {
  const espLabel = especialidade === '__all__' ? 'Consolidado (todas as especialidades)' : especialidade;
  // Grupos (Fisioterapia/Fonoaudiologia) não têm um card de upload próprio — cada
  // subespecialidade é enviada no seu próprio card, então o link genérico basta.
  const uploadHref = (especialidade === '__all__' || isGrupo(especialidade))
    ? '/uploads?tab=visitas'
    : `/uploads?tab=visitas&esp=${encodeURIComponent(especialidade)}`;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  // States matching HTML dashboard
  const [refDate, setRefDate] = useState(() => getLocalTodayISO());
  const [search, setSearch] = useState('');
  const [espec, setEspec] = useState('');
  const [freq, setFreq] = useState('');
  const [status, setStatus] = useState('todas'); // 'todas', 'vencida', 'pendente'
  const [group, setGroup] = useState('paciente'); // 'paciente', 'especialidade'
  const [statusAtend, setStatusAtend] = useState('');
  const [hideTest, setHideTest] = useState(true);
  
  // Expanded patients list
  const [expandedPatients, setExpandedPatients] = useState({});

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        // Importação automática do IW (consolidada): se for mais nova que a planilha
        // manual dessa especialidade, o detalhe usa as linhas dela.
        if (especialidade !== '__all__') {
          const cons = await ultimoConsolidado();
          if (cons) {
            const dataCons = dataDoUpload(cons.uploadedAt);
            const qm = query(collection(db, COLL), where('especialidade', '==', especialidade), orderBy('uploadedAt', 'desc'), limit(1));
            const manual = isGrupo(especialidade) ? null : (await getDocs(qm)).docs[0]?.data();
            const dataManual = manual ? dataDoUpload(manual.uploadedAt) : null;
            const linhas = linhasDaEspecialidade(cons, especialidade);
            if (linhas.length && (!dataManual || (dataCons && dataCons > dataManual))) {
              setRows(linhas);
              setMeta({ fileName: cons.fileName || 'IW (automático)', rowCount: linhas.length, uploadedAt: dataCons?.toLocaleString('pt-BR') || '', uploadedBy: cons.uploadedBy });
              setLoading(false);
              return;
            }
          }
        }

        if (especialidade !== '__all__' && isGrupo(especialidade)) {
          // Fisioterapia/Fonoaudiologia: junta a planilha mais recente de CADA
          // subespecialidade do grupo (ex: Fisioterapeuta + Bobath + RTA) numa visão só.
          const grupo = getGrupoConfig(especialidade);
          const q = query(
            collection(db, COLL),
            where('especialidade', '>=', grupo.prefixo),
            where('especialidade', '<', grupo.prefixo + ''),
            orderBy('uploadedAt', 'desc'),
            limit(40)
          );
          const snap = await getDocs(q);
          const bySubEsp = new Map();
          snap.docs.forEach(d => {
            const data = d.data();
            if (!bySubEsp.has(data.especialidade)) bySubEsp.set(data.especialidade, data);
          });
          const docsArr = [...bySubEsp.values()];
          if (docsArr.length > 0) {
            const rowsArray = docsArr.flatMap(d => d.data || []);
            const dates = docsArr.map(d => {
              const raw = d.uploadedAt;
              return !raw ? null : (typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw));
            }).filter(Boolean);
            const maxDate = dates.length ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
            setRows(rowsArray);
            setMeta({
              fileName: docsArr.map(d => d.fileName).filter(Boolean).join(' + '),
              rowCount: rowsArray.length,
              uploadedAt: maxDate?.toLocaleString('pt-BR') || '',
              uploadedBy: [...new Set(docsArr.map(d => d.uploadedBy).filter(Boolean))].join(', '),
            });
          } else {
            setRows([]);
            setMeta(null);
          }
          setLoading(false);
          return;
        }

        // Só o upload mais recente (dessa especialidade) é usado como retrato atual —
        // uploads antigos não entram mais no cálculo, para o painel sempre refletir o
        // último arquivo carregado. "__all__" preserva o comportamento antigo (consolidado,
        // sem filtro de especialidade) para uploads legados/automáticos via FTP.
        const q = especialidade !== '__all__'
          ? query(collection(db, COLL), where('especialidade', '==', especialidade), orderBy('uploadedAt', 'desc'), limit(1))
          : query(collection(db, COLL), orderBy('uploadedAt', 'desc'), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          const data = d.data();
          const raw = data.uploadedAt;
          const uploadedAt = !raw ? null : (typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw));
          const rowsArray = data.data || [];

          setRows(rowsArray);
          setMeta({
            fileName: data.fileName || '',
            rowCount: rowsArray.length,
            uploadedAt: uploadedAt?.toLocaleString('pt-BR') || '',
            uploadedBy: data.uploadedBy,
          });
        } else {
          setRows([]);
          setMeta(null);
        }
      } catch (e) {
        console.error("Erro ao carregar visitas:", e);
      }
      setLoading(false);
    };
    load();
  }, [especialidade]);

  // ---- Normalization: each row -> record ----
  const records = useMemo(() => {
    return rows.map(r => {
      const realiz = pick(r, COL_ALIASES.re).trim();
      const ate = parseDate(pick(r, COL_ALIASES.ate));
      return {
        pac: pick(r, COL_ALIASES.pac).trim(),
        op: pick(r, COL_ALIASES.op).trim(),
        st: pick(r, COL_ALIASES.st).trim(),
        esp: pick(r, COL_ALIASES.esp).trim() || '(sem especialidade)',
        fr: pick(r, COL_ALIASES.fr).trim() || '(sem frequência)',
        prof: pick(r, COL_ALIASES.prof).trim(),
        fil: (f => (/home ?senior|pronto ?s[eê]nior/i.test(f) ? 'Vitalcare' : f))(pick(r, COL_ALIASES.fil).trim()), // HomeSenior = Vitalcare
        deD: parseDate(pick(r, COL_ALIASES.de)),
        ateD: ate,
        realizada: !!realiz,
        isTest: /teste|test\b/i.test(pick(r, COL_ALIASES.pac)),
      };
    });
  }, [rows]);

  // ---- Core aggregation given current filters ----
  const computed = useMemo(() => {
    const refParts = refDate.split('-');
    const ref = new Date(+refParts[0], +refParts[1] - 1, +refParts[2]);

    let faltas = records.filter(r => !r.realizada);
    let realiz = records.filter(r => r.realizada);

    if (hideTest) {
      faltas = faltas.filter(r => !r.isTest);
      realiz = realiz.filter(r => !r.isTest);
    }

    // tag each falta with overdue/pending vs ref
    const taggedFaltas = faltas.map(r => ({
      ...r,
      over: r.ateD ? r.ateD < ref : false
    }));

    const q = search.toLowerCase().trim();
    const filtered = taggedFaltas.filter(r => {
      if (espec && r.esp !== espec) return false;
      if (freq && r.fr !== freq) return false;
      if (status === 'vencida' && !r.over) return false;
      if (status === 'pendente' && r.over) return false;
      if (statusAtend && (r.st || '').toUpperCase().trim() !== statusAtend.toUpperCase().trim()) return false;
      if (q) {
        const matchPac = r.pac.toLowerCase().includes(q);
        const matchProf = r.prof.toLowerCase().includes(q);
        const matchOp = r.op.toLowerCase().includes(q);
        if (!matchPac && !matchProf && !matchOp) return false;
      }
      return true;
    });

    return { faltas: taggedFaltas, realiz, filtered };
  }, [records, refDate, hideTest, search, espec, freq, status, statusAtend]);

  // ---- specialty, frequency and care status lists for select dropdowns ----
  const selectOptions = useMemo(() => {
    const activeFaltas = records.filter(r => !r.realizada && !(hideTest && r.isTest));
    const esps = [...new Set(activeFaltas.map(r => r.esp))].sort();
    const freqs = [...new Set(activeFaltas.map(r => r.fr))].sort();
    const statusAtends = [...new Set(activeFaltas.map(r => r.st).filter(Boolean))].sort();
    return { esps, freqs, statusAtends };
  }, [records, hideTest]);

  // ---- specialty and frequency breakdowns (custom bar charts) ----
  const breakdowns = useMemo(() => {
    const byE = {};
    const byF = {};
    
    computed.faltas.forEach(r => {
      byE[r.esp] = (byE[r.esp] || 0) + 1;
      byF[r.fr] = (byF[r.fr] || 0) + 1;
    });

    const sortedE = Object.entries(byE).sort((a, b) => b[1] - a[1]);
    const sortedF = Object.entries(byF).sort((a, b) => b[1] - a[1]);

    return {
      byEspec: sortedE,
      byFreq: sortedF
    };
  }, [computed.faltas]);

  // ---- patients grouped & ranked ----
  const patientCardsData = useMemo(() => {
    const groups = {};
    computed.filtered.forEach(r => {
      (groups[r.pac] = groups[r.pac] || []).push(r);
    });

    const realizByPac = {};
    computed.realiz.forEach(r => {
      realizByPac[r.pac] = (realizByPac[r.pac] || 0) + 1;
    });

    const list = Object.entries(groups).map(([pac, vs]) => {
      const venc = vs.filter(v => v.over).length;
      return {
        pac,
        vs,
        venc,
        pend: vs.length - venc,
        total: vs.length,
        done: realizByPac[pac] || 0,
        op: vs[0].op,
        st: vs[0].st,
        fil: vs[0].fil
      };
    });

    // sort: total faltas desc, then vencidas desc, then name
    list.sort((a, b) => b.total - a.total || b.venc - a.venc || a.pac.localeCompare(b.pac));
    return list;
  }, [computed.filtered, computed.realiz]);

  // ---- group by specialty data ----
  const specialtyGroupData = useMemo(() => {
    const groups = {};
    computed.filtered.forEach(r => {
      (groups[r.esp] = groups[r.esp] || []).push(r);
    });

    const entries = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);

    return entries.map(([esp, vs]) => {
      const byPac = {};
      vs.forEach(v => {
        (byPac[v.pac] = byPac[v.pac] || []).push(v);
      });
      const pacs = Object.entries(byPac).sort((a, b) => b[1].length - a[1].length);
      const venc = vs.filter(v => v.over).length;
      return { esp, vs, pacs, venc };
    });
  }, [computed.filtered]);

  // ---- active filter pills ----
  const activeFilters = useMemo(() => {
    const pills = [];
    if (espec) pills.push({ text: `Especialidade: ${espec}`, onClear: () => setEspec('') });
    if (freq) pills.push({ text: `Frequência: ${freq}`, onClear: () => setFreq('') });
    if (status !== 'todas') pills.push({ text: `Situação: ${status === 'vencida' ? 'Vencidas' : 'Pendentes'}`, onClear: () => setStatus('todas') });
    if (statusAtend) pills.push({ text: `Status Atendimento: ${statusAtend}`, onClear: () => setStatusAtend('') });
    if (search) pills.push({ text: `Busca: "${search}"`, onClear: () => setSearch('') });
    return pills;
  }, [espec, freq, status, statusAtend, search]);

  const handleClearAll = () => {
    setEspec('');
    setFreq('');
    setStatus('todas');
    setStatusAtend('');
    setSearch('');
  };

  const handleBarClick = (dim, val) => {
    if (dim === 'espec') {
      setEspec(prev => prev === val ? '' : val);
    } else {
      setFreq(prev => prev === val ? '' : val);
    }
  };

  const toggleExpandPatient = (pacName) => {
    setExpandedPatients(prev => ({
      ...prev,
      [pacName]: !prev[pacName]
    }));
  };

  // ---- CSV Export ----
  const handleExportCSV = () => {
    const sorted = computed.filtered.slice().sort((a, b) => {
      return a.pac.localeCompare(b.pac) || (a.over === b.over ? 0 : a.over ? -1 : 1);
    });

    const head = ['Paciente', 'Operadora', 'Filial', 'Status', 'Especialidade', 'Frequencia', 'Profissional', 'Janela inicio', 'Janela fim', 'Situacao', 'Dias'];
    const lines = [head.join(';')];
    const refParts = refDate.split('-');
    const ref = new Date(+refParts[0], +refParts[1] - 1, +refParts[2]);

    sorted.forEach(v => {
      const sit = v.over ? 'Vencida' : 'Pendente';
      const dias = v.ateD ? daysBetween(ref, v.ateD) : '';
      const deStr = v.deD ? fmtFull(v.deD) : '';
      const ateStr = v.ateD ? fmtFull(v.ateD) : '';

      const line = [
        v.pac,
        v.op,
        v.fil,
        v.st,
        v.esp,
        v.fr,
        v.prof,
        deStr,
        ateStr,
        sit,
        dias
      ];
      // escape double quotes and wrap in quotes
      const escapedLine = line.map(x => `"${String(x ?? '').replace(/"/g, '""')}"`).join(';');
      lines.push(escapedLine);
    });

    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `faltas_visitas_${refDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = useMemo(() => {
    const totalFaltas = computed.faltas.length;
    const totalRealizadas = computed.realiz.length;
    const totalVisits = totalFaltas + totalRealizadas;
    const pct = totalVisits ? Math.round((totalRealizadas / totalVisits) * 100) : 0;
    
    const countPacFalta = new Set(computed.faltas.map(r => r.pac)).size;
    const totalPacientes = new Set(records.filter(r => !(hideTest && r.isTest)).map(r => r.pac)).size;
    const vencidasCount = computed.faltas.filter(r => r.over).length;
    const pendentesCount = totalFaltas - vencidasCount;

    return {
      pacFalta: countPacFalta,
      totalPacientes,
      totalFaltas,
      totalRealizadas,
      pct,
      vencidas: vencidasCount,
      pendentes: pendentesCount
    };
  }, [computed, records, hideTest]);

  const VoltarBtn = () => (
    <button onClick={onVoltar} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent',
      color: '#0F766E', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', padding: '4px 0', marginBottom: 14,
    }}>
      <ArrowLeft size={15} /> Especialidades
    </button>
  );

  if (loading) {
    return (
      <div style={{ padding: '30px 40px 0' }}>
        <VoltarBtn />
        <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: '0.95rem', fontWeight: 600 }}>
          <Clock size={36} className="animate-spin" style={{ margin: '0 auto 12px', color: '#0F766E' }} />
          Carregando informações do painel...
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div style={{ padding: '30px 20px 0' }}>
        <VoltarBtn />
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 54, marginBottom: 16 }}>📋</div>
          <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Nenhum dado de "{espLabel}" disponível</h3>
          <p style={{ color: '#64748b', marginBottom: 24, fontSize: '0.9rem' }}>Suba a planilha desta especialidade na Central de Uploads.</p>
          <Link to={uploadHref} style={{
            display: 'inline-block', padding: '10px 20px', background: '#0F766E', color: '#fff',
            borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: '0.85rem'
          }}>
            Ir para Central de Uploads
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <style>{`
        .visitas-wrap { max-width: 100%; padding: 0 30px 80px; }
        .visitas-header { padding: 30px 0 22px; }
        .brandrow { display: flex; align-items: center; gap: 13px; flex-wrap: wrap; }
        .logo-visitas {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(150deg, #0F766E, #13948a);
          display: grid; place-items: center; flex-shrink: 0;
          box-shadow: 0 4px 14px rgba(15,118,110,.32);
        }
        .titles-visitas h1 { font-size: 21px; font-weight: 700; letter-spacing: -.02em; line-height: 1.1; color: #1e293b; }
        .titles-visitas p { color: #64748b; font-size: 13px; margin-top: 2px; }
        .headtools { margin-left: auto; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .refbox {
          display: flex; align-items: center; gap: 8px; background: #fff;
          border: 1px solid #e2e8f0; border-radius: 8px; padding: 7px 11px;
          box-shadow: 0 1px 2px rgba(20,33,48,.04);
        }
        .refbox label { font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
        .refbox input { border: none; font-family: inherit; font-size: 13px; color: #1e293b; background: transparent; outline: none; font-weight: 600; }
        
        .btn-visitas {
          display: inline-flex; align-items: center; gap: 7px; border: 1px solid #cbd5e1; background: #fff;
          color: #334155; font-family: inherit; font-size: 13px; font-weight: 600; padding: 8px 13px; border-radius: 8px;
          cursor: pointer; box-shadow: 0 1px 2px rgba(20,33,48,.04); transition: all 0.16s;
        }
        .btn-visitas:hover { border-color: #0F766E; color: #0b5a54; background: #e6f4f2; }
        .btn-visitas-primary { background: #0F766E; border-color: #0F766E; color: #fff; }
        .btn-visitas-primary:hover { background: #0b5a54; color: #fff; }
        
        .datasrc { font-size: 12px; color: #94a3b8; margin-top: 10px; display: flex; align-items: center; gap: 7px; }
        .datasrc b { color: #334155; font-weight: 600; }
        .dot-live { width: 7px; height: 7px; border-radius: 50%; background: #0F766E; box-shadow: 0 0 0 3px #e6f4f2; }
        
        .kpis-visitas { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 8px 0 18px; }
        .kpi-visitas { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 17px 18px; box-shadow: 0 1px 2px rgba(20,33,48,.04); position: relative; overflow: hidden; }
        .kpi-visitas .cap { font-size: 11.5px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; display: flex; align-items: center; gap: 6px; }
        .kpi-visitas .val { font-size: 34px; font-weight: 700; letter-spacing: -.02em; margin-top: 7px; line-height: 1; color: #1e293b; }
        .kpi-visitas .sub { font-size: 12px; color: #64748b; margin-top: 6px; }
        .kpi-visitas .accent { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; }
        .kpi-visitas.k-pac .accent { background: #0F766E; }
        .kpi-visitas.k-falta .accent { background: #17212E; }
        .kpi-visitas.k-venc .accent { background: #D4351C; }
        .kpi-visitas.k-pend .accent { background: #B5790B; }
        .kpi-visitas.k-venc .val { color: #D4351C; }
        .kpi-visitas.k-pend .val { color: #B5790B; }
        
        .break-visitas { display: grid; grid-template-columns: 1.25fr 1fr; gap: 14px; margin-bottom: 20px; }
        .panel-visitas { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 16px 18px; box-shadow: 0 1px 2px rgba(20,33,48,.04); }
        .panel-visitas h3 { font-size: 12.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #334155; margin-bottom: 13px; display: flex; align-items: center; gap: 8px; }
        .panel-visitas h3 .n { margin-left: auto; font-size: 11px; color: #94a3b8; font-weight: 600; }
        .bars-visitas { display: flex; flex-direction: column; gap: 9px; }
        .bar-visitas { display: grid; grid-template-columns: 128px 1fr 34px; align-items: center; gap: 11px; cursor: pointer; }
        .bar-visitas .lb { font-size: 12.5px; color: #334155; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 7px; }
        .bar-visitas .track { height: 9px; background: #f8fafc; border-radius: 5px; overflow: hidden; border: 1px solid #e2e8f0; }
        .bar-visitas .fill { height: 100%; border-radius: 5px; transition: width .5s cubic-bezier(.2,.7,.2,1); }
        .bar-visitas .vv { font-size: 12.5px; font-weight: 700; text-align: right; color: #1e293b; }
        .bar-visitas:hover .lb { color: #0b5a54; }
        .bar-visitas.active-bar .lb { color: #0b5a54; font-weight: 700; }
        .tagdot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
        
        .toolbar-visitas {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 13px 15px;
          box-shadow: 0 1px 2px rgba(20,33,48,.04); display: flex; align-items: center; gap: 11px;
          flex-wrap: wrap; margin-bottom: 18px; position: sticky; top: 10px; z-index: 30;
        }
        .search-visitas { flex: 1; min-width: 200px; display: flex; align-items: center; gap: 9px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0 12px; }
        .search-visitas input { flex: 1; border: none; background: transparent; outline: none; font-family: inherit; font-size: 13.5px; color: #1e293b; padding: 9px 0; }
        
        .selctl-visitas { position: relative; }
        .selctl-visitas select {
          appearance: none; font-family: inherit; font-size: 13px; font-weight: 500; color: #334155; background: #f8fafc;
          border: 1px solid #e2e8f0; border-radius: 8px; padding: 9px 32px 9px 12px; cursor: pointer; outline: none; transition: all 0.15s;
        }
        .selctl-visitas select:hover { border-color: #cbd5e1; }
        .selctl-visitas select:focus { border-color: #0F766E; }
        .selctl-visitas::after {
          content: ""; position: absolute; right: 12px; top: 50%; width: 6px; height: 6px;
          border-right: 1.6px solid #64748b; border-bottom: 1.6px solid #64748b; transform: translateY(-65%) rotate(45deg); pointer-events: none;
        }
        
        .segwrap-visitas { display: inline-flex; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 3px; gap: 2px; }
        .seg-visitas {
          font-family: inherit; font-size: 12.5px; font-weight: 600; color: #64748b; background: transparent;
          border: none; padding: 6px 12px; border-radius: 7px; cursor: pointer; transition: all 0.15s; white-space: nowrap;
        }
        .seg-visitas.on { background: #fff; color: #0b5a54; box-shadow: 0 1px 2px rgba(20,33,48,.04); }
        
        .chk-visitas { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; color: #334155; font-weight: 500; cursor: pointer; user-select: none; }
        .chk-visitas input { width: 16px; height: 16px; accent-color: #0F766E; cursor: pointer; }
        
        .activefilters { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; align-items: center; }
        .fpill { display: inline-flex; align-items: center; gap: 7px; background: #e6f4f2; color: #0b5a54; border: 1px solid #bdf0e9; font-size: 12px; font-weight: 600; padding: 5px 8px 5px 11px; border-radius: 20px; }
        .fpill button {
          border: none; background: rgba(11,90,84,.14); color: #0b5a54; width: 16px; height: 16px; border-radius: 50%;
          cursor: pointer; font-size: 13px; line-height: 1; display: grid; place-items: center;
        }
        .fpill button:hover { background: rgba(11,90,84,.3); }
        .clearall { font-size: 12px; color: #64748b; font-weight: 600; cursor: pointer; text-decoration: underline; background: none; border: none; }
        
        .grid-visitas { display: grid; grid-template-columns: repeat(auto-fill, minmax(370px, 1fr)); gap: 15px; }
        .pcard-visitas { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 1px 2px rgba(20,33,48,.04); overflow: hidden; display: flex; flex-direction: column; transition: all .2s; position: relative; }
        .pcard-visitas:hover { box-shadow: 0 8px 40px rgba(20,33,48,.14); transform: translateY(-2px); }
        
        .sevbar { position: absolute; left: 0; top: 0; bottom: 0; width: 5px; }
        .pc-head { padding: 15px 17px 13px 20px; border-bottom: 1px solid #e2e8f0; display: flex; gap: 12px; align-items: flex-start; }
        .rank { font-size: 13px; font-weight: 700; color: #cbd5e1; min-width: 30px; padding-top: 2px; }
        .pc-id { flex: 1; min-width: 0; }
        .pc-name { font-size: 15px; font-weight: 700; line-height: 1.25; color: #1e293b; }
        .pc-meta { font-size: 11.5px; color: #64748b; margin-top: 3px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .pc-meta .sep { width: 3px; height: 3px; border-radius: 50%; background: #cbd5e1; }
        .statuschip { font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 2px 7px; border-radius: 5px; background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
        
        .bignum { text-align: center; flex-shrink: 0; padding-left: 6px; }
        .bignum .n { font-size: 30px; font-weight: 700; line-height: 1; }
        .bignum .l { font-size: 9.5px; color: #64748b; font-weight: 600; text-transform: uppercase; margin-top: 2px; }
        
        .sevsplit { display: flex; gap: 6px; padding: 9px 17px 9px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; flex-wrap: wrap; }
        .minichip { font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 6px; display: inline-flex; align-items: center; gap: 5px; }
        .minichip.v { background: #fef2f2; color: #D4351C; }
        .minichip.p { background: #fffbeb; color: #B5790B; }
        .minichip.r { background: #e6f4f2; color: #0f766e; }
        
        .visits { padding: 6px 8px 8px; display: flex; flex-direction: column; }
        .visit { display: grid; grid-template-columns: auto 1fr auto; gap: 11px; align-items: start; padding: 9px 11px; border-radius: 9px; transition: all .12s; }
        .visit:hover { background: #f8fafc; }
        .vdot { width: 9px; height: 9px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; box-shadow: 0 0 0 3px #fff; }
        .vbody { min-width: 0; }
        .vspec { font-size: 13.5px; font-weight: 600; color: #1e293b; line-height: 1.25; }
        .vfreq { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: #0F766E; background: #e6f4f2; padding: 1px 7px; border-radius: 5px; margin-left: 6px; }
        
        .vsub { font-size: 11.5px; color: #64748b; margin-top: 3px; display: flex; gap: 7px; flex-wrap: wrap; align-items: center; }
        .vsub .sep { width: 3px; height: 3px; border-radius: 50%; background: #cbd5e1; }
        .vstatus { font-size: 11px; font-weight: 700; white-space: nowrap; text-align: right; padding-top: 1px; }
        .vstatus.v { color: #D4351C; }
        .vstatus.p { color: #B5790B; }
        .vstatus small { display: block; font-weight: 500; font-size: 10px; color: #cbd5e1; }
        
        .morebtn { margin: 2px 11px 6px; background: #f8fafc; border: 1px dashed #cbd5e1; color: #64748b; font-size: 12px; font-weight: 600; padding: 7px; border-radius: 8px; cursor: pointer; transition: all .15s; text-align: center; border-radius: 8px; }
        .morebtn:hover { color: #0b5a54; border-color: #0F766E; background: #e6f4f2; }
        
        .specgroup { margin-bottom: 22px; }
        .specgroup-h { display: flex; align-items: center; gap: 10px; margin-bottom: 11px; padding-bottom: 9px; border-bottom: 2px solid #e2e8f0; }
        .specgroup-h .gd { width: 12px; height: 12px; border-radius: 4px; }
        .specgroup-h h3 { font-size: 15px; font-weight: 700; color: #1e293b; }
        .specgroup-h .gc { margin-left: auto; font-size: 14px; font-weight: 700; color: #64748b; }
        
        .empty-visitas { text-align: center; padding: 70px 20px; color: #64748b; }
        .empty-visitas svg { width: 46px; height: 46px; color: #cbd5e1; margin-bottom: 14px; }
        .empty-visitas h3 { font-size: 16px; color: #334155; font-weight: 600; margin-bottom: 5px; }
        .empty-visitas p { font-size: 13px; max-width: 340px; margin: 0 auto; }
        
        .visitas-footer { margin-top: 38px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; line-height: 1.7; }
        .visitas-footer b { color: #64748b; font-weight: 600; }
      `}</style>

      <div className="visitas-wrap">
        {/* Header */}
        <header className="visitas-header">
          <VoltarBtn />
          <div className="brandrow">
            <div className="logo-visitas">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
                <path d="M3 12h3l2 5 4-12 2 7h4" />
              </svg>
            </div>
            <div className="titles-visitas">
              <h1>{espLabel}</h1>
              <p>Quem não recebeu qual visita — por especialidade e frequência</p>
            </div>
            <div className="headtools">
              <div className="refbox">
                <label htmlFor="refdate">Data ref.</label>
                <input
                  type="date"
                  id="refdate"
                  value={refDate}
                  onChange={e => setRefDate(e.target.value)}
                />
              </div>
              <Link to={uploadHref} style={{ textDecoration: 'none' }}>
                <button className="btn-visitas">
                  <Upload size={14} />
                  Carregar CSV
                </button>
              </Link>
              <button className="btn-visitas btn-visitas-primary" onClick={handleExportCSV}>
                <FileText size={14} />
                Exportar faltas
              </button>
            </div>
          </div>
          {meta && (
            <div className="datasrc">
              <span className="dot-live"></span>
              Fonte: <b>{meta.fileName}</b> · <b>{meta.rowCount}</b> agendamentos · upload em <b>{meta.uploadedAt}</b> por <b>{meta.uploadedBy}</b>
            </div>
          )}
        </header>

        {/* KPIs */}
        <section className="kpis-visitas">
          <div className="kpi-visitas k-pac">
            <span className="accent"></span>
            <div className="cap"><span className="tagdot" style={{ background: '#0F766E' }}></span>Pacientes com falta</div>
            <div className="val">{kpis.pacFalta}</div>
            <div className="sub">de {kpis.totalPacientes} pacientes na base</div>
          </div>
          <div className="kpi-visitas k-falta">
            <span className="accent"></span>
            <div className="cap"><span className="tagdot" style={{ background: '#1e293b' }}></span>Visitas em falta</div>
            <div className="val">{kpis.totalFaltas}</div>
            <div className="sub">{kpis.totalRealizadas} realizadas · {kpis.pct}% de cobertura</div>
          </div>
          <div className="kpi-visitas k-venc">
            <span className="accent"></span>
            <div className="cap"><span className="tagdot" style={{ background: '#D4351C' }}></span>Vencidas</div>
            <div className="val">{kpis.venc}</div>
            <div className="sub">prazo já passou da data de ref.</div>
          </div>
          <div className="kpi-visitas k-pend">
            <span className="accent"></span>
            <div className="cap"><span className="tagdot" style={{ background: '#B5790B' }}></span>Pendentes</div>
            <div className="val">{kpis.pendentes}</div>
            <div className="sub">ainda dentro do prazo</div>
          </div>
        </section>

        {/* Breakdowns */}
        <section className="break-visitas">
          <div className="panel-visitas">
            <h3>Faltas por especialidade <span className="n">{breakdowns.byEspec.length} especialidades</span></h3>
            <div className="bars-visitas">
              {breakdowns.byEspec.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '12.5px', padding: '6px 0' }}>Sem faltas para os filtros atuais.</div>
              ) : (
                breakdowns.byEspec.slice(0, 9).map(([name, count]) => {
                  const maxVal = breakdowns.byEspec[0][1];
                  const col = specColor(name);
                  const isActive = espec === name;
                  const pctWidth = maxVal ? Math.max(4, (count / maxVal) * 100) : 0;
                  return (
                    <div
                      key={name}
                      className={`bar-visitas ${isActive ? 'active-bar' : ''}`}
                      onClick={() => handleBarClick('espec', name)}
                    >
                      <span className="lb">
                        <span className="tagdot" style={{ background: col, marginRight: 6 }}></span>
                        {name}
                      </span>
                      <span className="track">
                        <span className="fill" style={{ width: `${pctWidth}%`, background: col }}></span>
                      </span>
                      <span className="vv">{count}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="panel-visitas">
            <h3>Faltas por frequência <span className="n">{breakdowns.byFreq.length} frequências</span></h3>
            <div className="bars-visitas">
              {breakdowns.byFreq.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '12.5px', padding: '6px 0' }}>Sem faltas para os filtros atuais.</div>
              ) : (
                breakdowns.byFreq.slice(0, 9).map(([name, count]) => {
                  const maxVal = breakdowns.byFreq[0][1];
                  const col = 'var(--pendente)';
                  const isActive = freq === name;
                  const pctWidth = maxVal ? Math.max(4, (count / maxVal) * 100) : 0;
                  return (
                    <div
                      key={name}
                      className={`bar-visitas ${isActive ? 'active-bar' : ''}`}
                      onClick={() => handleBarClick('freq', name)}
                    >
                      <span className="lb">{name}</span>
                      <span className="track">
                        <span className="fill" style={{ width: `${pctWidth}%`, background: col }}></span>
                      </span>
                      <span className="vv">{count}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>

        {/* Toolbar */}
        <div className="toolbar-visitas">
          <div className="search-visitas">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar paciente, profissional ou operadora..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="selctl-visitas">
            <select value={espec} onChange={e => setEspec(e.target.value)}>
              <option value="">Todas especialidades</option>
              {selectOptions.esps.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div className="selctl-visitas">
            <select value={freq} onChange={e => setFreq(e.target.value)}>
              <option value="">Todas frequências</option>
              {selectOptions.freqs.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="selctl-visitas">
            <select value={statusAtend} onChange={e => setStatusAtend(e.target.value)}>
              <option value="">Todos status de atendimento</option>
              {selectOptions.statusAtends.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="segwrap-visitas">
            <button className={`seg-visitas ${status === 'todas' ? 'on' : ''}`} onClick={() => setStatus('todas')}>Todas</button>
            <button className={`seg-visitas ${status === 'vencida' ? 'on' : ''}`} onClick={() => setStatus('vencida')}>Vencidas</button>
            <button className={`seg-visitas ${status === 'pendente' ? 'on' : ''}`} onClick={() => setStatus('pendente')}>Pendentes</button>
          </div>
          <div className="segwrap-visitas">
            <button className={`seg-visitas ${group === 'paciente' ? 'on' : ''}`} onClick={() => setGroup('paciente')}>Por paciente</button>
            <button className={`seg-visitas ${group === 'especialidade' ? 'on' : ''}`} onClick={() => setGroup('especialidade')}>Por especialidade</button>
          </div>
          <label className="chk-visitas">
            <input
              type="checkbox"
              checked={hideTest}
              onChange={e => setHideTest(e.target.checked)}
            />
            Ocultar testes
          </label>
        </div>

        {/* Active Filter Pills */}
        {activeFilters.length > 0 && (
          <div className="activefilters">
            {activeFilters.map(pill => (
              <span key={pill.text} className="fpill">
                <span>{pill.text}</span>
                <button onClick={pill.onClear}>×</button>
              </span>
            ))}
            <button className="clearall" onClick={handleClearAll}>Limpar tudo</button>
          </div>
        )}

        {/* Result Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 13 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>
            {group === 'paciente' ? 'Pacientes com visitas em falta' : 'Faltas agrupadas por especialidade'}
          </h2>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
            {group === 'paciente'
              ? (patientCardsData.length ? `${patientCardsData.length} paciente${patientCardsData.length !== 1 ? 's' : ''} · ${computed.filtered.length} visita${computed.filtered.length !== 1 ? 's' : ''} em falta` : '')
              : (specialtyGroupData.length ? `${specialtyGroupData.length} especialidade${specialtyGroupData.length !== 1 ? 's' : ''} · ${computed.filtered.length} visita${computed.filtered.length !== 1 ? 's' : ''} em falta` : '')
            }
          </span>
        </div>

        {/* Output Grid */}
        {group === 'paciente' ? (
          /* Render by Patient */
          patientCardsData.length === 0 ? (
            <div className="empty-visitas">
              <AlertCircle size={46} />
              <h3>Nenhuma visita em falta</h3>
              <p>Nenhum agendamento corresponde aos filtros atuais. Ajuste os filtros ou limpe a busca.</p>
            </div>
          ) : (
            <div className="grid-visitas">
              {patientCardsData.map((p, index) => {
                const rank = index + 1;
                const severityColor = p.total >= 6 ? 'var(--vencida)' : p.total >= 3 ? 'var(--pendente)' : 'var(--brand)';
                const SHOWN = 4;
                const isExpanded = !!expandedPatients[p.pac];
                const visitsSorted = p.vs.slice().sort((a, b) => {
                  if (a.over !== b.over) return a.over ? -1 : 1;
                  return (a.ateD?.getTime() || 0) - (b.ateD?.getTime() || 0);
                });
                const visibleVisits = isExpanded ? visitsSorted : visitsSorted.slice(0, SHOWN);
                const hiddenCount = Math.max(0, visitsSorted.length - SHOWN);

                return (
                  <div key={p.pac} className="pcard-visitas">
                    <span className="sevbar" style={{ background: severityColor }}></span>
                    <div className="pc-head">
                      <span className="rank">#{rank}</span>
                      <div className="pc-id">
                        <div className="pc-name" title={p.pac}>{p.pac}</div>
                        <div className="pc-meta">
                          {p.op && <span>{p.op}</span>}
                          {p.op && p.fil && <span className="sep"></span>}
                          {p.fil && <span>Filial {p.fil}</span>}
                          <span className="sep"></span>
                          <span className="statuschip" style={getStatusChipStyle(p.st)}>{p.st || '—'}</span>
                        </div>
                      </div>
                      <div className="bignum">
                        <div className="n" style={{ color: severityColor }}>{p.total}</div>
                        <div className="l">em falta</div>
                      </div>
                    </div>
                    <div className="sevsplit">
                      {p.venc > 0 && <span className="minichip v">● {p.venc} vencida{p.venc !== 1 ? 's' : ''}</span>}
                      {p.pend > 0 && <span className="minichip p">● {p.pend} pendente{p.pend !== 1 ? 's' : ''}</span>}
                      <span className="minichip r">✓ {p.done} realizada{p.done !== 1 ? 's' : ''}</span>
                    </div>
                    
                    <div className="visits">
                      {visibleVisits.map((v, vidx) => {
                        const col = specColor(v.esp);
                        const deStr = v.deD ? fmtShort(v.deD) : '';
                        const ateStr = v.ateD ? fmtShort(v.ateD) : '';
                        const refParts = refDate.split('-');
                        const ref = new Date(+refParts[0], +refParts[1] - 1, +refParts[2]);
                        
                        let statusText;
                        if (v.over) {
                          const d = v.ateD ? daysBetween(ref, v.ateD) : null;
                          statusText = (
                            <div className="vstatus v">
                              Vencida
                              {d !== null && <small>há {d} dia{d !== 1 ? 's' : ''}</small>}
                            </div>
                          );
                        } else {
                          const d = v.ateD ? daysBetween(v.ateD, ref) : null;
                          statusText = (
                            <div className="vstatus p">
                              Pendente
                              {d !== null && <small>{d} dia{d !== 1 ? 's' : ''} p/ vencer</small>}
                            </div>
                          );
                        }

                        return (
                          <div key={vidx} className="visit">
                            <span className="vdot" style={{ background: col }}></span>
                            <div className="vbody">
                              <div className="vspec">
                                {v.esp}
                                <span className="vfreq">{v.fr}</span>
                              </div>
                              <div className="vsub">
                                {(deStr || ateStr) && <span>janela {deStr} – {ateStr}</span>}
                                {v.prof && <span className="sep"></span>}
                                {v.prof && <span>{v.prof}</span>}
                              </div>
                            </div>
                            {statusText}
                          </div>
                        );
                      })}
                    </div>
                    {hiddenCount > 0 && (
                      <button className="morebtn" onClick={() => toggleExpandPatient(p.pac)}>
                        {isExpanded ? 'Ver menos' : `+ ${hiddenCount} outra${hiddenCount > 1 ? 's' : ''} visita${hiddenCount > 1 ? 's' : ''} em falta`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Render by Specialty Group */
          specialtyGroupData.length === 0 ? (
            <div className="empty-visitas">
              <AlertCircle size={46} />
              <h3>Nenhuma visita em falta</h3>
              <p>Nenhum agendamento corresponde aos filtros atuais. Ajuste os filtros ou limpe a busca.</p>
            </div>
          ) : (
            specialtyGroupData.map(({ esp, vs, pacs, venc }) => {
              const col = specColor(esp);
              return (
                <div key={esp} className="specgroup">
                  <div className="specgroup-h">
                    <span className="gd" style={{ background: col }}></span>
                    <h3>{esp}</h3>
                    <span className="gc">
                      {vs.length} falta{vs.length !== 1 ? 's' : ''}
                      {venc > 0 && ` · ${venc} vencida${venc !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                  <div className="grid-visitas">
                    {pacs.map(([pac, pv], pidx) => {
                      const vc = pv.filter(v => v.over).length;
                      const pcTotal = pv.length;
                      
                      return (
                        <div key={pac} className="pcard-visitas">
                          <span className="sevbar" style={{ background: col }}></span>
                          <div className="pc-head">
                            <div className="pc-id">
                              <div className="pc-name">{pac}</div>
                              <div className="pc-meta">
                                <span>{pv[0].op || '—'}</span>
                                {pv[0].op && pv[0].fil && <span className="sep"></span>}
                                {pv[0].fil && <span>Filial {pv[0].fil}</span>}
                                <span className="sep"></span>
                                <span className="statuschip" style={getStatusChipStyle(pv[0].st)}>{pv[0].st || '—'}</span>
                              </div>
                            </div>
                            <div className="bignum">
                              <div className="n" style={{ color: col }}>{pcTotal}</div>
                              <div className="l">em falta</div>
                            </div>
                          </div>
                          {vc > 0 && (
                            <div className="sevsplit">
                              <span className="minichip v">● {vc} vencida{vc !== 1 ? 's' : ''}</span>
                              {pcTotal - vc > 0 && (
                                <span className="minichip p">● {pcTotal - vc} pendente{pcTotal - vc !== 1 ? 's' : ''}</span>
                              )}
                            </div>
                          )}
                          <div className="visits">
                            {pv.map((v, vidx) => {
                              const deStr = v.deD ? fmtShort(v.deD) : '';
                              const ateStr = v.ateD ? fmtShort(v.ateD) : '';
                              const refParts = refDate.split('-');
                              const ref = new Date(+refParts[0], +refParts[1] - 1, +refParts[2]);

                              let statusText;
                              if (v.over) {
                                const d = v.ateD ? daysBetween(ref, v.ateD) : null;
                                statusText = (
                                  <div className="vstatus v">
                                    Vencida
                                    {d !== null && <small>há {d} dia{d !== 1 ? 's' : ''}</small>}
                                  </div>
                                );
                              } else {
                                const d = v.ateD ? daysBetween(v.ateD, ref) : null;
                                statusText = (
                                  <div className="vstatus p">
                                    Pendente
                                    {d !== null && <small>{d} dia{d !== 1 ? 's' : ''} p/ vencer</small>}
                                  </div>
                                );
                              }

                              return (
                                <div key={vidx} className="visit">
                                  <span className="vdot" style={{ background: col }}></span>
                                  <div className="vbody">
                                    <div className="vspec">
                                      {v.esp}
                                      <span className="vfreq">{v.fr}</span>
                                    </div>
                                    <div className="vsub">
                                      {(deStr || ateStr) && <span>janela {deStr} – {ateStr}</span>}
                                      {v.prof && <span className="sep"></span>}
                                      {v.prof && <span>{v.prof}</span>}
                                    </div>
                                  </div>
                                  {statusText}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )
        )}

        {/* Footer */}
        <footer className="visitas-footer">
          <b>Como ler:</b> cada visita em falta é um agendamento sem data de realização.
          <br />
          <b>Vencida</b> = o prazo "agendar até" já passou da data de referência; <b>Pendente</b> = ainda dentro do prazo.
          <br />
          Os pacientes são ordenados pelo total de faltas (maior primeiro).
          <br />
          Para acumular vários dias, carregue o CSV consolidado na Central de Uploads — o cálculo é refeito automaticamente.
        </footer>
      </div>
    </div>
  );
}
